import { css } from '@emotion/css';
import React, {
  useCallback,
  useMemo,
  useEffect,
  useState,
  useRef,
  createContext,
  useContext,
  useImperativeHandle,
  MouseEvent,
} from 'react';
import { Tooltip } from 'antd';
import {
  Descendant,
  NodeEntry,
  Range,
  Text,
  createEditor,
  Transforms,
  Editor,
  Element,
  Path,
} from 'slate';
import { withHistory } from 'slate-history';
import {
  Editable,
  RenderLeafProps,
  Slate,
  withReact,
  useFocused,
  useSlate,
} from 'slate-react';
import { Button, Icon, Menu, Portal } from './mdEditorComponent';
import { CustomEditor, CustomTextKey } from './custom-types';

// 定义修改记录的接口
interface Replacement {
  original: {
    text: string;
    range: Range;
  };
  replacement: string;
}

// 扩展组件接口，增加ref暴露的方法
export interface MarkdownEditorRef {
  applyReplacement: (originalText: string, replacementText: string) => void;
  getCurrentSelection: () => Range | null;
  getEditor: () => CustomEditor;
  highlightSensitiveWords: (
    sensitiveWords: Array<{
      word: string;
      reason: string;
      start_pos?: number;
      end_pos?: number;
    }>,
  ) => void;
  clearSensitiveWords: () => void;
}

interface MarkdownEditorProps {
  value: Descendant[];
  onChange?: (value: Descendant[]) => void;
  onTextSelectAdjust?: (selectedText: string) => void;
}

interface MarkdownEditorContextType {
  currentSelectedText: string | null;
  triggerParentOnTextSelected: (text: string, selection: any) => void;
}
const MarkdownEditorCustomContext =
  createContext<MarkdownEditorContextType | null>(null);

// Convert string with newlines to Slate document structure
export const stringToSlateValue = (text: string): Descendant[] => {
  return text.split('\n').map((line) => ({
    type: 'paragraph',
    children: [{ text: line }],
  }));
};

// Convert Slate document structure back to string with newlines
export const slateValueToString = (nodes: Descendant[]): string => {
  const processedLines: string[] = [];

  for (const node of nodes) {
    if (Element.isElement(node)) {
      // Assuming top-level elements are line containers (e.g., paragraphs)
      const lineContent = node.children
        .map((child) => {
          if (Text.isText(child)) {
            if (child.del === true) {
              return ''; // Ignore text marked with del
            }
            return child.text;
          }
          return ''; // Ignore non-text children within a paragraph (e.g., inline elements if any)
        })
        .join('');

      // 保留所有行，包括空行，以确保换行符不被吞掉
      processedLines.push(lineContent);
    } else if (Text.isText(node)) {
      // Handle potential top-level text nodes, though less common for typical line-based structures
      // This part might be less relevant if your structure is always paragraph-based from stringToSlateValue
      if (node.del !== true) {
        processedLines.push(node.text);
      } else {
        // 即使是删除的文本，也保留空行以维持结构
        processedLines.push('');
      }
    }
    // Other top-level node types could be handled here if necessary
  }
  return processedLines.join('\n');
};

const MarkdownEditor = React.forwardRef<MarkdownEditorRef, MarkdownEditorProps>(
  ({ value, onChange, onTextSelectAdjust }, ref) => {
    // Convert incoming string to Slate format
    // const slateValue = useMemo(() => stringToSlateValue(value), [value]);

    // 新增状态管理
    const [selectedText, setSelectedText] = useState<string | null>(null);

    // 只保存最近一次高亮的文本
    const [highlightedText, setHighlightedText] = useState<string | null>(null);

    // 保存当前选择范围
    const [currentSelection, setCurrentSelection] = useState<Range | null>(
      null,
    );

    // 保存所有的修改记录
    const [replacements, setReplacements] = useState<Replacement[]>([]);

    const editor = useMemo(
      () => withHistory(withReact(createEditor())) as CustomEditor,
      [],
    );

    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
      applyReplacement: (originalText: string, replacementText: string) => {
        // 检查当前选择范围是否存在且匹配原文本
        if (currentSelection && selectedText === originalText) {
          // 添加到修改记录
          setReplacements((prev) => [
            ...prev,
            {
              original: {
                text: originalText,
                range: currentSelection,
              },
              replacement: replacementText,
            },
          ]);

          const insertionPoint = currentSelection.focus;
          Transforms.select(editor, insertionPoint);
          Transforms.collapse(editor, { edge: 'focus' });
          Editor.insertBreak(editor);
          // 插入替换文本（加粗）
          Transforms.insertNodes(editor, {
            text: '' + replacementText,
            add: true,
          });
        }
      },
      getCurrentSelection: () => {
        return currentSelection;
      },
      getEditor: () => {
        return editor;
      },
      highlightSensitiveWords: (
        sensitiveWords: Array<{
          word: string;
          reason: string;
          start_pos?: number;
          end_pos?: number;
        }>,
      ) => {
        // 先清除之前的敏感词标记
        Editor.withoutNormalizing(editor, () => {
          const [match] = Editor.nodes(editor, {
            at: [],
            match: (n) => Text.isText(n) && n.sensitive === true,
          });

          if (match) {
            // 清除所有敏感词标记
            Transforms.setNodes(
              editor,
              { sensitive: undefined, sensitiveReason: undefined },
              {
                at: [],
                match: (n) => Text.isText(n) && n.sensitive === true,
                split: true,
              },
            );
          }

          // 应用新的敏感词标记
          const content = Editor.string(editor, []);

          // 处理敏感词，区分有位置信息和没有位置信息的情况
          const wordsWithPositions: any[] = [];

          sensitiveWords.forEach((item) => {
            if (item.start_pos !== undefined && item.end_pos !== undefined) {
              // 有位置信息，直接使用
              wordsWithPositions.push(item);
            } else {
              // 没有位置信息，在编辑器内容中查找所有匹配
              const keyword = item.word;
              let searchStartIndex = 0;
              let foundIndex = -1;

              while (
                (foundIndex = content.indexOf(keyword, searchStartIndex)) !== -1
              ) {
                wordsWithPositions.push({
                  word: keyword,
                  reason: item.reason,
                  start_pos: foundIndex,
                  end_pos: foundIndex + keyword.length,
                });

                // 移动搜索起始位置到下一个可能的位置
                searchStartIndex = foundIndex + 1;
              }
            }
          });

          // 按位置降序排列，避免标记时位置偏移
          const sortedWords = [...wordsWithPositions].sort(
            (a, b) => b.start_pos - a.start_pos,
          );

          sortedWords.forEach((item) => {
            try {
              // 将字符偏移量转换为 Slate 的 Path 和 offset
              let currentOffset = 0;
              let startPath: Path | null = null;
              let startOffset = 0;
              let endPath: Path | null = null;
              let endOffset = 0;

              for (const [node, path] of Editor.nodes(editor, {
                at: [],
                match: (n) => Text.isText(n),
              })) {
                if (Text.isText(node)) {
                  const nodeLength = node.text.length;

                  // 找到起始位置
                  if (
                    startPath === null &&
                    currentOffset + nodeLength >= item.start_pos
                  ) {
                    startPath = path;
                    startOffset = item.start_pos - currentOffset;
                  }

                  // 找到结束位置
                  if (
                    endPath === null &&
                    currentOffset + nodeLength >= item.end_pos
                  ) {
                    endPath = path;
                    endOffset = item.end_pos - currentOffset;
                    break;
                  }

                  currentOffset += nodeLength;
                }
              }

              if (startPath && endPath) {
                const range = {
                  anchor: { path: startPath, offset: startOffset },
                  focus: { path: endPath, offset: endOffset },
                };

                Transforms.setNodes(
                  editor,
                  {
                    sensitive: true,
                    sensitiveReason: item.reason,
                  },
                  {
                    at: range,
                    match: (n) => Text.isText(n),
                    split: true,
                  },
                );
              }
            } catch (error) {
              console.error('Error highlighting sensitive word:', error);
            }
          });
        });
      },
      clearSensitiveWords: () => {
        Editor.withoutNormalizing(editor, () => {
          Transforms.setNodes(
            editor,
            { sensitive: undefined, sensitiveReason: undefined },
            {
              at: [],
              match: (n) => Text.isText(n) && n.sensitive === true,
              split: true,
            },
          );
        });
      },
    }));

    const contextValue: MarkdownEditorContextType = {
      currentSelectedText: selectedText, // 从 MarkdownEditor 的 state 获取
      triggerParentOnTextSelected: (text: string, selection: any) => {
        setCurrentSelection(selection);
        setSelectedText(text);
        if (onTextSelectAdjust) {
          onTextSelectAdjust(text);
        }
      },
    };

    const renderLeaf = useCallback(
      (props: RenderLeafProps) => <Leaf {...props} />,
      [],
    );

    // Handle external value changes
    useEffect(() => {
      // const newSlateValue = stringToSlateValue(value);
      // Only update if content has actually changed
      if (JSON.stringify(editor.children) !== JSON.stringify(value)) {
        editor.children = value;
        // Reset selection to start of document
        Transforms.select(editor, Editor.start(editor, []));
        // Clear replacements when content changes
        setReplacements([]);
      }
    }, [editor, value]);

    // Handle change events from editor
    const handleChange = (newValue: Descendant[]) => {
      // Convert Slate value back to string and notify parent
      if (onChange) {
        // const newStringValue = slateValueToString(newValue);
        onChange(newValue);
      }
    };

    return (
      <MarkdownEditorCustomContext.Provider value={contextValue}>
        <div style={{ position: 'relative' }}>
          <Slate editor={editor} initialValue={value} onChange={handleChange}>
            <HoveringToolbar />
            <Editable
              renderLeaf={renderLeaf}
              placeholder="Write some markdown..."
              className={css`
                min-height: 200px;
                padding: 8px;
                border: 1px solid #d9d9d9;
                border-radius: 4px;
              `}
              // onSelect={handleSelection}
            />
          </Slate>
        </div>
      </MarkdownEditorCustomContext.Provider>
    );
  },
);

// 修改Leaf组件以支持高亮、删除线和加粗
const Leaf = ({ attributes, children, leaf }: RenderLeafProps) => {
  const leafElement = (
    <span
      {...attributes}
      className={css`
        font-weight: ${leaf.bold || leaf.title ? 'bold' : 'normal'};
        font-style: ${leaf.italic ? 'italic' : 'normal'};
        background-color: ${leaf.highlighted
          ? '#ffeb3b !important'
          : leaf.sensitive
          ? '#ff4d4f !important'
          : 'transparent'};
        text-decoration: ${leaf.underlined
          ? 'underline'
          : leaf.strike || leaf.del
          ? 'line-through'
          : 'none'};
        ${leaf.del &&
        css`
          background-color: #ffebe9;
          color: #999999;
        `}
        ${leaf.add &&
        css`
          background-color: #dafbe1;
        `}
        ${leaf.blockquote &&
        css`
          display: inline-block;
          border-left: 2px solid #ddd;
          padding-left: 10px;
          color: #aaa;
          font-style: italic;
        `}
        ${leaf.code &&
        css`
          font-family: monospace;
          background-color: #eee;
          padding: 3px;
        `}
        ${leaf.sensitive &&
        css`
          color: #fff !important;
          border-radius: 3px;
          padding: 1px 2px;
        `}
      `}
    >
      {children}
    </span>
  );

  // 如果是敏感词，包装一个Tooltip
  if (leaf.sensitive && leaf.sensitiveReason) {
    return (
      <Tooltip title={`敏感词: ${leaf.sensitiveReason}`} placement="top">
        {leafElement}
      </Tooltip>
    );
  }

  return leafElement;
};

const HoveringToolbar = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const editor = useSlate();
  const inFocus = useFocused();

  useEffect(() => {
    const el = ref.current;
    const { selection } = editor;

    if (!el) {
      return;
    }

    if (
      !selection ||
      !inFocus ||
      Range.isCollapsed(selection) ||
      Editor.string(editor, selection) === ''
    ) {
      el.removeAttribute('style');
      return;
    }

    const domSelection = window.getSelection();
    const domRange = domSelection!.getRangeAt(0);
    const rect = domRange.getBoundingClientRect();
    el.style.opacity = '1';
    el.style.zIndex = '9999999';
    el.style.top = `${rect.top + window.pageYOffset - el.offsetHeight}px`;
    el.style.left = `${
      rect.left + window.pageXOffset - el.offsetWidth / 2 + rect.width / 2
    }px`;
  });

  return (
    <Portal>
      <Menu
        ref={ref}
        className={css`
          padding: 8px 7px 6px;
          position: absolute;
          z-index: 1;
          top: -10000px;
          left: -10000px;
          margin-top: -6px;
          opacity: 0;
          background-color: #222;
          border-radius: 4px;
          transition: opacity 0.75s;
        `}
        onMouseDown={(e: MouseEvent) => {
          // prevent toolbar from taking focus away from editor
          e.preventDefault();
        }}
      >
        <FormatButton format="highlighted" icon="AI改写" />
      </Menu>
    </Portal>
  );
};

const removeMarkGlobally = (editor: CustomEditor, format: CustomTextKey) => {
  // Get all text nodes in the editor.
  // Editor.nodes(editor, { at: [], match: Text.isText, universal: true })
  // iterates over all nodes at all depths.
  const textNodes = Editor.nodes(editor, {
    at: [], // Start at the root of the document
    match: (n) => Text.isText(n), // Only match text nodes
    universal: true, // Iterate over all nodes, not just those in the current selection
  });

  // For each text node that has the mark, unset it.
  for (const [node, path] of textNodes) {
    if (node[format]) {
      // Check if the mark exists on the node
      Transforms.unsetNodes(editor, format, {
        at: path, // Specify the path of the node to modify
        match: (n) => Text.isText(n) && n[format] === true, // Ensure we only unset on this specific node if it has the mark
      });
    }
  }
};

export const toggleMark = (editor: CustomEditor, format: CustomTextKey) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isMarkActive = (editor: CustomEditor, format: CustomTextKey) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

interface FormatButtonProps {
  format: CustomTextKey;
  icon: string;
}

const FormatButton = ({ format, icon }: FormatButtonProps) => {
  const editor = useSlate();
  const { triggerParentOnTextSelected } = useContext(
    MarkdownEditorCustomContext,
  ) as MarkdownEditorContextType;

  const handleButtonClick = () => {
    const { selection } = editor;
    if (selection) {
      triggerParentOnTextSelected(Editor.string(editor, selection), selection);
      removeMarkGlobally(editor, format);
      toggleMark(editor, format);
    }
  };

  return (
    <Button reversed onClick={handleButtonClick}>
      <Icon>{icon}</Icon>
    </Button>
  );
};

export default MarkdownEditor;
